import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AtSign, MessageSquareText, Send, Trash2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function highlight(text, mentions = []) {
  if (!mentions.length) return text;
  const names = mentions.map((m) => m.name).sort((a, b) => b.length - a.length);
  const rx = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  const parts = text.split(rx);
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="rounded bg-black px-1 text-[#FACC15]">@{p}</strong> : p));
}

export function NotesThread({ ticketId, notes, onUpdated }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [picked, setPicked] = useState({});
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data), staleTime: 300000 });
  const sorted = [...(notes || [])].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tickets"] });
    qc.invalidateQueries({ queryKey: ["mentions"] });
  };

  const match = /(^|\s)@([\w ]{0,30})$/.exec(text);
  const suggestions = match ? team.filter((u) => u.id !== user.id && u.name.toLowerCase().includes(match[2].trim().toLowerCase())).slice(0, 5) : [];
  const pick = (u) => {
    setText(text.slice(0, match.index + match[1].length) + `@${u.name} `);
    setPicked({ ...picked, [u.name]: u.id });
  };
  const mentionIds = Object.entries(picked).filter(([name]) => text.includes(`@${name}`)).map(([, id]) => id);

  const add = useMutation({
    mutationFn: () => api.post(`/tickets/${ticketId}/notes`, { text: text.trim(), mentions: mentionIds }).then((r) => r.data),
    onSuccess: (data) => {
      setText("");
      setPicked({});
      onUpdated(data);
      invalidate();
      toast.success(mentionIds.length ? `Note added · ${mentionIds.length} teammate(s) notified` : "Note added");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (noteId) => api.delete(`/tickets/${ticketId}/notes/${noteId}`).then((r) => r.data),
    onSuccess: (data) => {
      onUpdated(data);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-4 border-t-2 border-dashed border-zinc-300 pt-6" data-testid="notes-thread">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold">
        <MessageSquareText className="h-5 w-5" /> Internal notes
        <span className="rounded-full bg-black px-2 py-0.5 text-xs text-white" data-testid="notes-count">{sorted.length}</span>
      </h3>
      <p className="-mt-2 text-sm text-zinc-600">Team-only. Type <span className="font-mono font-bold">@</span> to mention a teammate — they'll get a "needs your attention" badge.</p>
      <div className="relative">
        <div className="flex gap-2">
          <textarea
            className="field-input min-h-[64px] flex-1 !py-2.5"
            placeholder={`Leave a note as ${user.name}… use @ to mention`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) add.mutate();
            }}
            data-testid="note-input"
            maxLength={2000}
          />
          <button className="btn-primary self-end !px-4" onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid="add-note-button" title="Add note (Ctrl+Enter)">
            <Send className="h-4 w-4" />
          </button>
        </div>
        {suggestions.length > 0 && (
          <ul className="absolute left-0 top-full z-20 mt-1 w-72 overflow-hidden rounded-xl border-2 border-black bg-white brut-shadow-sm" data-testid="mention-suggestions">
            {suggestions.map((u) => (
              <li key={u.id}>
                <button type="button" onClick={() => pick(u)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold hover:bg-[#FEF9C3]" data-testid={`mention-option-${u.id}`}>
                  <AtSign className="h-4 w-4" /> {u.name} <span className="ml-auto text-xs uppercase text-zinc-500">{u.role}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="notes-empty">No notes yet.</p>
      ) : (
        <ol className="space-y-3" data-testid="notes-list">
          {sorted.map((n) => {
            const mine = n.author_id === user.id;
            return (
              <li key={n.id} className={`rounded-xl border-2 p-3 ${mine ? "border-black bg-[#FEF9C3]" : "border-zinc-300 bg-white"}`} data-testid={`note-${n.id}`}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-bold">{mine ? "You" : n.author}</span>
                  <span className="flex items-center gap-2 text-zinc-500">
                    {fmtDate(n.created_at)}
                    {(mine || user.role === "admin") && (
                      <button onClick={() => remove.mutate(n.id)} className="text-zinc-500 hover:text-[#DC2626]" title="Delete note" data-testid={`delete-note-${n.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </span>
                </div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm">{highlight(n.text, n.mentions)}</p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
